// SalesTableGrouped.js
import React, { useState, useRef, useEffect } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, CircularProgress, Typography, Box } from "@mui/material";

import { format } from "date-fns";
// Placeholder for external utility (ensure this is imported correctly in your project)
// import groupSalesThreeLevels from "../utils/groupSalesThreeLevels";

// Assuming these helpers are passed down from the parent
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const TOTAL_COLUMNS = 12; // INV NO, DATE, CUSTOMER, SALES MAN, ACCOUNT, CASH/CREDIT, AMOUNT_EXCLVAT, AMOUNT (8 total)
const TOTAL_VALUE_COLUMNS = 2; // AMOUNT_EXCLVAT and AMOUNT


const InvoiceProfitItemWise = ({ rows, grandColumnTotals, loading, groupSalesThreeLevels, loadNextPage, hasMore }) => {


    const tableContainerRef = useRef(null);

    // 🚨 2. Define the scroll detection logic
    const handleTableScroll = () => {

        console.log("scrolled-->")
        const currentTarget = tableContainerRef.current;

        if (!currentTarget || loading || !hasMore || rows.length === 0) return;

        const { scrollTop, scrollHeight, clientHeight } = currentTarget;

        // Load the next page when reaching the bottom (with a 10px buffer)
        if (scrollHeight - scrollTop <= clientHeight + 10) {
            loadNextPage();
        }
    };



    console.log("data----> received in component", rows)
    const [collapsedState, setCollapsedState] = useState({});


    const toggleCollapse = (key) => {
        setCollapsedState(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const isRowVisible = (row) => {
        if (!row.DEPTNO) return true;
        const deptKey = row.DEPTNO.trim();

        if (row.isGroupHeader) {
            return true;
        }
        if (row.isSubHeader || row.CASHCRED || !row["INV NO"]) { // isSubHeader, isSubSubHeader, or detail row
            if (collapsedState[deptKey]) return false;

            if (row.TYPE) {
                const typeKey = `${deptKey}-${row.TYPE.trim()}`;
                if (row.isSubHeader) return true;

                if (row.CASHCRED || !row["INV NO"]) { // isSubSubHeader or detail row
                    if (collapsedState[typeKey]) return false;

                    if (row.CASHCRED) {
                        const cashCredKey = `${typeKey}-${row.CASHCRED.trim()}`;
                        if (row.isSubSubHeader) return true;

                        // Detail row is only hidden if parent CASHCRED is collapsed
                        if (collapsedState[cashCredKey]) return false;
                    }
                }
            }
        }
        return true;
    };


    const renderRow = (row, index) => {
        if (!isRowVisible(row)) return null;

        const isGroupHeader = row.isGroupHeader;
        const isSubHeader = row.isSubHeader;
        const isSubSubHeader = row.isSubSubHeader;
        const isDetailRow = !isGroupHeader && !isSubHeader && !isSubSubHeader;

        const deptKey = row.DEPTNO ? row.DEPTNO.trim() : null;
        const typeKey = row.TYPE && deptKey ? `${deptKey}-${row.TYPE.trim()}` : null;
        const cashCredKey = row.CASHCRED && typeKey ? `${typeKey}-${row.CASHCRED.trim()}` : null;

        // 1. Group Header (Level 1)
        if (isGroupHeader) {
            const isCollapsed = collapsedState[deptKey];
            return (
                <TableRow key={`dept-header-${index}`} onClick={() => toggleCollapse(deptKey)} sx={{ cursor: 'pointer', backgroundColor: '#e0f7fa' }}>
                    <TableCell colSpan={TOTAL_COLUMNS - TOTAL_VALUE_COLUMNS} style={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px' }}>
                        {isCollapsed ? '▶️' : '▼'} {`Department Total: ${row.DEPTNO}`}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row.totalAmountExclVAT)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row.totalAmount)}</TableCell>
                </TableRow>
            );
        }

        // 2. Type Sub-Header (Level 2)
        if (isSubHeader) {
            const isCollapsed = collapsedState[typeKey];
            return (
                <TableRow key={`type-header-${index}`} onClick={() => toggleCollapse(typeKey)} sx={{ cursor: 'pointer', backgroundColor: '#f0f4c3' }}>
                    <TableCell colSpan={TOTAL_COLUMNS - TOTAL_VALUE_COLUMNS} style={{ fontWeight: '600', padding: '6px 16px', paddingLeft: '30px' }}>
                        {isCollapsed ? '▶️' : '▼'} {`Type Sub-Total: ${row.TYPE}`}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: '600' }}>{formatNumber(row.totalAmountExclVAT)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: '600' }}>{formatNumber(row.totalAmount)}</TableCell>
                </TableRow>
            );
        }

        // 3. CASHCRED Sub-Sub-Header (Level 3)
        if (isSubSubHeader) {
            const isCollapsed = collapsedState[cashCredKey];
            return (
                <TableRow key={`cashcred-header-${index}`} onClick={() => toggleCollapse(cashCredKey)} sx={{ cursor: 'pointer', backgroundColor: '#fff8e1' }}>
                    <TableCell colSpan={TOTAL_COLUMNS - TOTAL_VALUE_COLUMNS} style={{ fontWeight: '500', padding: '4px 16px', paddingLeft: '60px' }}>
                        {isCollapsed ? '▶️' : '▼'} {`CASH/CREDIT Sub-Total: ${row.CASHCRED}`}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: '500' }}>{formatNumber(row.totalAmountExclVAT)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: '500' }}>{formatNumber(row.totalAmount)}</TableCell>
                </TableRow>
            );
        }

        // 4. Detail Row (Transactional) - 8 columns
        if (isDetailRow) {
            return (
                <TableRow key={`detail-row-${index}`} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell>{row["INV NO"]}</TableCell>
                    <TableCell>{format(new Date(row.DATE), "dd-MM-yyyy")}</TableCell>
                    <TableCell>{row.CODE}</TableCell>
                    <TableCell>{row.DESCRIPTION}</TableCell>
                    <TableCell>{row.QTY}</TableCell>

                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row["GROSS SALE"])}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row.DISC)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row["NET SALES"])}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row.COST)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row.PROFIT)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row["%AGE"])}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row["VAT AMT"])}</TableCell>



                </TableRow>
            );
        }
        return null;
    };

    return (
        <TableContainer
            ref={tableContainerRef}
            onScroll={handleTableScroll} // ⬅️ ATTACH SCROLL HANDLER HERE
            sx={{
                maxHeight: 'calc(100vh - 350px)',
                overflowY: 'auto', width: '100%'
            }}>
            <Table stickyHeader aria-label="grouped sales report">
                <TableHead>
                    <TableRow>
                        <TableCell style={{ minWidth: 100 }}>INV NO</TableCell>
                        <TableCell style={{ minWidth: 120 }}>INV DATE</TableCell>
                        <TableCell style={{ minWidth: 200 }}>CUSTOMER</TableCell>
                        <TableCell style={{ minWidth: 120 }}>SALES MAN</TableCell>
                        <TableCell style={{ minWidth: 150 }}>ACCOUNT</TableCell>

                        <TableCell style={{ minWidth: 120 }}>GROSS SALE</TableCell>
                        <TableCell style={{ minWidth: 120 }}>DISC</TableCell>
                        <TableCell style={{ minWidth: 120 }}>NET SALES</TableCell>
                        <TableCell style={{ minWidth: 120 }}>COST</TableCell>
                        <TableCell style={{ minWidth: 120 }}>PROFIT</TableCell>
                        <TableCell style={{ minWidth: 120 }}>%AGE</TableCell>
                        <TableCell style={{ minWidth: 120 }}>VAT AMT</TableCell>

                    </TableRow>
                </TableHead>

                <TableBody>
                    {rows.map(renderRow)}

                    {/* Load More Indicator */}
                    {hasMore && (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                            <Typography variant="caption" display="block">
                                Loading more data...
                            </Typography>
                        </Box>
                    )}
                    {!loading && rows.length === 0 && (
                        <TableRow><TableCell colSpan={TOTAL_COLUMNS} align="center"><Typography variant="subtitle1" color="textSecondary">No sales data found for the selected filters.</Typography></TableCell></TableRow>
                    )}
                </TableBody>

                {/* Fixed Grand Totals Footer */}
                {Object.keys(grandColumnTotals).length > 0 && (
                    <TableFooter>
                        <TableRow sx={{ backgroundColor: '#b3b3b3', borderTop: '3px double #000' }}>
                            <TableCell colSpan={TOTAL_COLUMNS - 7} align="right"
                                sx={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }}>
                                GRAND TOTAL:
                            </TableCell>
                            {/* Assuming grandColumnTotals only contains the columns used in this table */}
                            {/* {Object.keys(grandColumnTotals).map(key => (
                                <TableCell key={key} align="right"
                                    sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }}>
                                    {formatNumber(grandColumnTotals[key])}
                                </TableCell>
                            ))} */}
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }} align="right">{formatNumber(grandColumnTotals["GROSS SALE"])}</TableCell>
                             <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }} align="right">{formatNumber(grandColumnTotals["DISC"])}</TableCell>
                             <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }} align="right">{formatNumber(grandColumnTotals["NET SALES"])}</TableCell>
                             <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }} align="right">{formatNumber(grandColumnTotals["COST"])}</TableCell>
                             <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }} align="right">{formatNumber(grandColumnTotals["PROFIT"])}</TableCell>
                             <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }} align="right">{formatNumber(grandColumnTotals["%AGE"])}</TableCell>
                             <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }} align="right">{formatNumber(grandColumnTotals["VAT AMT"])}</TableCell>
                        </TableRow>
                    </TableFooter>
                )}
            </Table>

            {/* Loading and No Data Messages (using total columns) */}
            {loading && (
                <div style={{ display: "flex", justifyContent: 'center', alignItems: "center", width: "100%", height: "100%", position: "absolute", zIndex: 100, backgroundColor: "#ffffff80", top: "0px", left: "0px" }}>
                    <CircularProgress size={48} />
                </div>
            )}
        </TableContainer>
    );
};

export default InvoiceProfitItemWise;